import { useState, useEffect, useRef, useCallback } from 'react';
import { useMarkPageAsViewedMutation } from './usePageActions';

/**
 * Custom hook to track page views and mark pages as viewed
 * Shared between viewCourse (student view) and editCourse (instructor edit view)
 *
 * @param {Object} params - Hook parameters
 * @param {string|number} params.pageId - Current page ID
 * @param {number|string} params.courseId - Course ID (will be converted to number if string)
 * @param {Object} params.pageData - Page data object (must be truthy when page is loaded)
 * @param {boolean} params.isLocked - Whether the page is locked (only applies to student view, always false for edit mode)
 * @param {Function} params.onSuccess - Callback after successful tracking (optional, e.g., refetch progress in student view)
 * @returns {Object} - { hasMarkedViewed, resetTracking }
 */
export const usePageTracking = ({
  pageId,
  courseId,
  pageData,
  isLocked = false,
  onSuccess
}) => {
  const [hasMarkedViewed, setHasMarkedViewed] = useState(false);
  const [markPageAsViewedTrigger] = useMarkPageAsViewedMutation();

  // Store latest onSuccess in ref to avoid dependency issues
  // This prevents the effect from re-running when onSuccess reference changes
  const onSuccessRef = useRef(onSuccess);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  // Normalize courseId to number to ensure consistent API calls
  const normalizedCourseId = typeof courseId === 'string' ? parseInt(courseId, 10) : courseId;

  // Mark page as viewed when conditions are met
  // Defined outside useEffect for clarity and potential reusability
  const markAndUpdate = useCallback(async (cancelled) => {
    // Mark page as viewed if:
    // - We have page data loaded
    // - Page is not locked (only relevant for student view, always false in edit mode)
    // - We have a valid pageId (not diploma)
    // - We haven't already marked this page as viewed
    if (pageData && !isLocked && pageId && pageId !== 'diploma' && !hasMarkedViewed) {
      try {
        await markPageAsViewedTrigger({ pageId, courseId: normalizedCourseId }).unwrap();

        // Only update state if component is still mounted
        if (!cancelled.current) {
          setHasMarkedViewed(true);

          // Call optional success callback (e.g., refetch progress in student view)
          if (onSuccessRef.current) {
            onSuccessRef.current();
          }
        }
      } catch (error) {
        // Only log error if component is still mounted
        if (!cancelled.current) {
          console.error('Failed to mark page as viewed:', error);
        }
      }
    }
  }, [pageData, isLocked, pageId, markPageAsViewedTrigger, hasMarkedViewed, normalizedCourseId]);

  useEffect(() => {
    const cancelled = { current: false };

    markAndUpdate(cancelled);

    // Cleanup: prevent state updates if component unmounts
    return () => {
      cancelled.current = true;
    };
  }, [markAndUpdate]);

  // Reset hasMarkedViewed when page changes
  useEffect(() => {
    setHasMarkedViewed(false);
  }, [pageId]);

  return {
    hasMarkedViewed,
    resetTracking: useCallback(() => setHasMarkedViewed(false), [])
  };
};

